/* eslint-disable @typescript-eslint/naming-convention */
import { captureExceptionWithSentry } from '../../../sentry'
import * as React from 'react'
import getUuid from '../../../common/uuidString'
import Location from '../adLocations/Location'
import { AdFormats } from './../adFormat/adFormat'
import { ClientType, ScrapedCategory } from './../types'
import InstantArticles, { InstantArticle } from './components/InstantArticles/InstantArticles'
import Scraper from './components/Scraper'
import removeEmojis from '../../../common/emojiRemover'
import TrueNativeArticles from './components/TrueNativeArticles'

export interface Variation {
	uid: string // when approving/disapproving/editing AI suggestions, this is a random unique number for each suggestion
	id?: number
	variation_no: number // when approving/disapproving/editing AI suggestions, this is 0 for first suggestion, 1 for second, etc.

	comment?: string
	image?: string
	advertiser_name: string
	title: string
	description: string
	cta: string
	theme: string
	targetUrl: string
	impressionPixel: string
	suggestion: boolean
	imgformat?: string
	image_id?: string
	is_active: boolean
	hidden_locations?: number[]
	impressions?: number
	clicks?: number
	ctr?: number
	approved?: boolean
	dismissed?: boolean

	videoPath?: string
	videoId?: string
}

export interface Article {
	uid: string // this is empty for ai variation suggestions
	origin: string // this is empty for ai variation suggestions
	num?: number
	instant?: boolean
	instantArticle?: InstantArticleProperties
	startDate?: string
	endDate?: string
	maxClicks?: number | null
	settingsId?: number
	variations: Variation[]
	articleId?: number
	logo_id?: string
	logo?: string
	client_name?: string
	article_title?: string
}

export interface SaveResponse {
	status: 'OK' | 'error'
	message?: string
	data?: Variation | Article[]
}

export interface InstantArticleProperties {
	id?: number
	headline: string
	image: string
	introduction: string
	body: string
	image_id?: string
	iframe?: string
	client_name?: string
}

export interface Category {
	name: string
	category_id: string
	relevance: number
}

export const VariationLimits = {
	max_cta: 20,
	min_cta: 2,
	min_advertiser_name: 1,
	advertiser_name: 25,
	theme: 20,
	minTitle: 2,
	title: 70,
	minSummary: 5,
	summary: 120,
}

export const InstantArticleLimits = {
	minHeadline: 2,
	headline: 70,
	minIntroduction: 5,
	introduction: 120,
	minBody: 20,
	body: 20000,
}

export interface ArticleData {
	categories: string
	data: {
		description: string
		image?: string
		title: string
		url: string
		'image:secure_url'?: string
		video?: string
	}
	status: string
	url: string
}

interface ArticlesContainerProps {
	articles: Article[]
	instantArticles: InstantArticle
	addArticle: (arr: Article) => void
	updateArticle: (arr: Article, instant?: boolean) => void
	removeArticle: (uid: string, instant?: boolean) => void
	updateVariationField: (
		artUid: string,
		variationNum: number,
		field: keyof Variation,
		content: string,
		instant?: boolean,
	) => void
	spinner: boolean
	setSpinner: (bool: boolean) => void
	client?: ClientType
	setSuggestedCategories: (a: ScrapedCategory[]) => void
	selectedLocations?: Location[]
	adFormat: number
	updateInstantArticlesProperties: (values: { logo?: string; logo_id?: string }) => void
}

interface ArticlesContainerState {
	badUrlError: boolean
	badUrlMsg: string[]
	urls: string[]
	suggestedCategories: Category[]
}
class ArticlesContainer extends React.PureComponent<ArticlesContainerProps, ArticlesContainerState> {
	constructor(props: ArticlesContainerProps) {
		super(props)

		const params = window.location.search.slice(1).split('&')
		const url = params.find((p) => p.startsWith('url') && p.includes('='))

		this.state = {
			badUrlError: false,
			badUrlMsg: [],
			urls: [url ? url.split('=')[1] : ''],
			suggestedCategories: [],
		}
	}

	public componentDidMount() {
		this.getAds()
	}

	private setUrl = (index: number, value: string) => {
		if (index >= this.state.urls.length) {
			return
		}

		const urls = [...this.state.urls]
		urls[index] = value
		this.setState({ urls })
	}

	private addUrlInput = (e: any) => {
		e.preventDefault()
		const urls = [...this.state.urls, '']
		this.setState({ urls })
	}

	private removeUrlInput = (index: number) => {
		if (index >= this.state.urls.length) {
			return
		}

		const urls = [...this.state.urls.slice(0, index), ...this.state.urls.slice(index + 1)]
		this.setState({ urls })
	}

	private getAds = async (e?: any) => {
		if (e) {
			e.preventDefault()
		}
		const properUrls = this.state.urls.map((url) => url.trim()).filter((url) => url !== '')
		if (properUrls.length === 0) {
			return
		}
		this.setState({ badUrlError: false, badUrlMsg: [], urls: [''] })

		for (const url of properUrls) {
			this.scrapeUrl(url)
		}
	}

	private getUrlWithProtocol = (url: string): string => {
		return 'https://'.concat(url.replace(/^https?\:\/\//i, ''))
	}

	private createArticleVariations(
		url: string,
		// eslint-disable-next-line @typescript-eslint/naming-convention
		data?: { image?: string; title: string; description: string; url: string; 'image:secure_url'?: string },
	) {
		let i = 0
		const maxNumberVariation = data ? 3 : 1
		const result = []
		const image =
			this.props.adFormat !== AdFormats.Video ? (data ? data['image:secure_url'] ?? data.image : '') : ''
		while (i < maxNumberVariation) {
			result.push({
				uid: getUuid(),
				variation_no: i + 1,
				theme: '',
				image,
				advertiser_name: this.props.client ? this.props.client.org : '',
				title: data ? removeEmojis(data.title) : '',
				description: data ? removeEmojis(data.description) : '',
				cta: '',
				targetUrl: this.getUrlWithProtocol(url),
				impressionPixel: '',
				suggestion: (data && i === 0) || !data ? false : true,
				is_active: true,
			})
			i++
		}
		return result
	}

	private scrapeUrl = async (url: string) => {
		const fetchConfigObject: RequestInit = {
			credentials: 'same-origin',
			body: JSON.stringify({ values: { urls: [url] } }),
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			method: 'POST',
		}
		const article: Article = {
			uid: getUuid(),
			origin: url,
			variations: [],
			article_title: '',
		}
		try {
			this.props.addArticle(article)
			this.props.setSpinner(true)

			const responseObject = await fetch('/ad_campaigns/getUrlsMetadata', fetchConfigObject)
			const resp: ArticleData[] = await responseObject.json()
			const leikiCategories: Category[] = []
			const art = resp[0]
			const { status, data, categories } = art
			if (status === 'OK') {
				const articleVariations = this.createArticleVariations(url, data)
				for (const av of articleVariations) {
					article.article_title = av.title.substring(0, 70)
					article.variations.push(av)
				}
				leikiCategories.push(...JSON.parse(categories))
			} else {
				throw new Error('failed to scrape')
			}

			this.props.setSuggestedCategories(leikiCategories)
			this.props.updateArticle(article)
		} catch (error) {
			captureExceptionWithSentry(error)

			const articleVariations = this.createArticleVariations(url)
			for (const av of articleVariations) {
				article.variations.push(av)
			}
			this.props.updateArticle(article)
			this.setState((prevstate) => ({
				badUrlError: true,
				badUrlMsg: [...prevstate.badUrlMsg, url],
			}))
		}
		this.props.setSpinner(false)
	}

	public duplicateVariation = (articleId: string, variationNo: number, instant?: boolean) => {
		const article = instant
			? (this.props.instantArticles.articles![articleId] as Article)
			: this.props.articles.find((art) => art.uid === articleId)

		if (!article) {
			return
		}
		const variationToDuplicate = article.variations.find((variation) => {
			return variation.variation_no === variationNo
		})
		const newVariation = {
			...variationToDuplicate!,
			variation_no: article.variations.length + 1,
			uid: getUuid(),
		}
		delete newVariation.id
		article.variations = [...article.variations, newVariation]
		this.props.updateArticle(article)
	}

	public removeVariation = (articleId: string, variationNo: number, instant?: boolean) => {
		const article = instant
			? (this.props.instantArticles.articles![articleId] as Article)
			: this.props.articles.find((art) => art.uid === articleId)

		if (!article) {
			return
		}

		const variations = article.variations
			.filter((variation) => {
				return variation.variation_no !== variationNo
			})
			.map((vari, index) => {
				vari.variation_no = index + 1
				return vari
			})
		if (variations.length > 0) {
			article.variations = variations
			this.props.updateArticle(article, instant)
		} else {
			this.props.removeArticle(articleId, instant)
		}
	}

	public addVariation = (articleId: string, instant?: boolean) => {
		const article = instant
			? (this.props.instantArticles.articles![articleId] as Article)
			: this.props.articles.find((art) => art.uid === articleId)

		if (!article) {
			return
		}

		article.variations = [
			...article.variations,
			{
				uid: getUuid(),
				variation_no: article.variations.length + 1,
				theme: '',
				image: '',
				advertiser_name: this.props.client ? this.props.client.org : '',
				title: '',
				description: '',
				cta: '',
				targetUrl: article.origin,
				impressionPixel: '',
				suggestion: false,
				is_active: true,
			},
		]
		this.props.updateArticle(article, instant)
	}

	public render() {
		switch (this.props.adFormat) {
			case AdFormats.Instant:
				return (
					<InstantArticles
						instantArticles={this.props.instantArticles}
						updateVariationField={this.props.updateVariationField}
						duplicateVariation={this.duplicateVariation}
						removeVariation={this.removeVariation}
						addVariation={this.addVariation}
						updateArticle={this.props.updateArticle}
						selectedLocations={this.props.selectedLocations}
						client={this.props.client}
						addArticle={this.props.addArticle}
						updateInstantArticlesProperties={this.props.updateInstantArticlesProperties}
						adFormat={this.props.adFormat}
					/>
				)
			case AdFormats.TrueNative:
				return <TrueNativeArticles />
			case AdFormats.Content:
			case AdFormats.Product:
			default:
				return (
					<Scraper
						articles={this.props.articles}
						updateVariationField={this.props.updateVariationField}
						spinner={this.props.spinner}
						addUrlInput={this.addUrlInput}
						removeUrlInput={this.removeUrlInput}
						badUrlMsg={this.state.badUrlMsg}
						badUrlError={this.state.badUrlError}
						getAds={this.getAds}
						duplicateVariation={this.duplicateVariation}
						removeVariation={this.removeVariation}
						addVariation={this.addVariation}
						urls={this.state.urls}
						setUrl={this.setUrl}
						updateArticle={this.props.updateArticle}
						selectedLocations={this.props.selectedLocations}
						adFormat={this.props.adFormat}
					/>
				)
		}
	}
}
export default ArticlesContainer
